/**
 * Application constants and configuration
 */

// API configuration
export const API_CONFIG = {
  BASE_URL: import.meta.env.VITE_API_URL,
} as const;

// UI constants
export const UI_CONFIG = {
  COLORS: {
    SEGMENTS: ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 'bg-red-500'],
  },
  LIMITS: {
    MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
    MAX_COMPETITORS: 10,
    MIN_COMPETITORS: 3,
  },
} as const;

export const DEFAULTS = {
  AUDIENCE: {
    SIZE: 200,
    SIMULATION_TARGET: 'company' as const,
    AUDIENCE_SCOPE: 'all' as const,
  },
} as const;

// Validation constants
export const VALIDATION = {
  URL_REGEX: /^https?:\/\/.+\..+/,
} as const;

// "Book a call" CTA shown on the account-blocked screen (free tier disabled)
export const BOOK_A_CALL_URL =
  import.meta.env.VITE_BOOK_A_CALL_URL || 'https://calendly.com/ad-blue-pill/30min';

// Polling configuration for async operations
export const POLLING_CONFIG = {
  MAX_RETRIES: 120,
  BASE_DELAY: 1000,
  BACKOFF_MULTIPLIER: 1.5,
  MAX_DELAY: 10000,
  TIMEOUT_ERROR_CODE: 408,
  PROGRESS_INTERVAL_MS: 2000,
  PROGRESS_TIMEOUT_MS: 3600000,
} as const;

// Chat panel layout constants
export const CHAT_PANEL = {
  RESERVED_SPACE_FOR_UI: 125,
  MIN_CHAT_HEIGHT: 125,
  INITIAL_RESERVED_SPACE: 225,
} as const;

// Async followup analysis configuration
export const ASYNC_ANALYSIS_CONFIG = {
  POLL_INTERVAL_MS: 1000, // Frontend polling interval (milliseconds)
  POLL_TIMEOUT_MS: 60000, // Frontend total timeout (milliseconds)
  TASK_TIMEOUT_SECONDS: 60, // Maximum time for backend task to complete
  FINAL_QUESTION_POLL_TIMEOUT_MS: 20_000, // sits in front of /complete, so well under POLL_TIMEOUT_MS
} as const;

export const COMPLETION_CONFIG = { TIMEOUT_MS: 15_000, RETRIES: 3, BACKOFF_MS: 1_000 } as const;

// Voice-capture (survey microphone) configuration
export const VOICE_CAPTURE = {
  READINESS_TIMEOUT_MS: 5000, // max wait for first real audio chunk before failing to text
  TIMESLICE_MS: 250, // MediaRecorder.start() timeslice; first chunk = readiness signal
  MIN_BLOB_BYTES: 1000, // discard recordings smaller than this (meaningless to transcribe)
  // Silence before a spoken turn advances, and the default for `rapid_fire.silence_seconds`.
  SILENCE_ADVANCE_MS: 2000,
  // UNMEASURED starting guess for frameLevel()'s [0,1] scale, pending calibration. See ADR-0002 §1.
  SILENCE_LEVEL_THRESHOLD: 0.04,
  UNDO_WINDOW_MS: 3000, // how long "Undo" stays available after an auto-advance
  UNREADABLE_CLIP_LIMIT: 2, // unreadable clips on one question before typing is offered instead
  SILENT_TAKE_PEAK_DBFS: -50, // a take peaking below this holds no sound — measured, ADR-0014
  SILENT_TAKE_LIMIT: 2, // silent takes on one answer before typing is offered instead
  NO_SIGNAL_HINT_MS: 3000, // recording this long with no signal shows the "can't hear you" hint
  NO_SIGNAL_LEVEL: 0.005, // frameLevel() below this is no signal at all — ADR-0014
  // Backstop on holding a section for a bound transcript — ADR-0005.
  TRANSCRIPT_WAIT_CEILING_MS: 45000,
} as const;

// Analysis task states (must match backend constants)
export const ANALYSIS_TASK_STATES = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  SUCCESS: 'success',
  FAILED: 'failed',
} as const;
